<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="60%"
    data-testid="field-modal"
  >
    <v-tabs v-model="selectedTab" centered>
      <v-tab href="#tabGeneral">{{ $t("General") }}</v-tab>
      <v-tab
        href="#tabConfiguration"
        :key="'tabConfiguration'"
        v-show="field.type != null"
        >{{ $t("Configuration") }}</v-tab
      >
      <v-tab-item value="tabGeneral">
        <v-card flat class="my-0">
          <v-card-text>
            <v-text-field
              v-bind:label="$t('Name')"
              v-model="field.name"
              maxlength="100"
              :counter="100"
              :error-messages="nameErrors"
              @change="fieldNameChange"
              @input="$v.field.name.$touch()"
              @blur="$v.field.name.$touch()"
              data-testid="field-name"
            ></v-text-field>

            <v-select
              v-bind:label="$t('Type')"
              v-model="field.type"
              :items="fieldTypeList"
              item-text="text"
              item-value="id"
              :error-messages="typeErrors"
              :disabled="field.id > 0"
              data-testid="field-type"
            ></v-select>

            <v-combobox
              v-model="field.code"
              :items="defFields"
              :loading="isLoadingSearch"
              :search-input.sync="searchFields"
              hide-no-data
              hide-selected
              item-text="code"
              item-value="code"
              v-bind:label="$t('Code')"
              append-outer-icon="mdi-magnify"
              @click:append-outer="openSearch"
              :error-messages="codeErrors"
              @change="fieldCodeChange"
              :disabled="isEdit"
            ></v-combobox>

            <v-textarea
              v-bind:label="$t('Remarks')"
              v-model="field.remarks"
              data-testid="field-remarks"
            ></v-textarea>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item value="tabConfiguration">
        <StringField
          v-if="field.type == fieldType.String"
          :field.sync="field"
          :validator.sync="$v"
        />
        <DateTimeField
          v-if="field.type == fieldType.DateTime"
          :field.sync="field"
          :validator.sync="$v"
        />
        <NumericField
          v-if="field.type == fieldType.Numeric && !field.isNewValueFormat"
          :field.sync="field"
          :validator.sync="$v"
        />
        <NumericFieldISOCompliant
          v-if="field.type == fieldType.Numeric && field.isNewValueFormat"
          :field.sync="field"
          :validator.sync="$v"
        />
        <StaticListField
          v-if="field.type == fieldType.StaticList"
          :field.sync="field"
          :validator.sync="$v"
        />
        <ExternalTableField
          v-if="field.type == fieldType.DynamicList"
          :field.sync="field"
          :validator.sync="$v"
          :contentTypeFields="contentTypeFields"
        />
        <InternalTableField
          v-if="field.type == fieldType.DynamicTableList"
          :field.sync="field"
          :validator.sync="$v"
          :contentTypeFields="contentTypeFields"
        />
      </v-tab-item>
    </v-tabs>
    <FieldSearchModal :ref="modalSearchKey" @on-select="onFieldSelect" />
  </base-modal>
</template>
<script>
import { required, requiredIf, maxValue, minValue } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import snakeCase from 'lodash/snakeCase';

import ContentTypeService from '@/services/ContentTypeService';
import ContentTypeField from '@/ecm/models/contentType/ContentTypeField';
import Utils from '@/ecm/common/Utils';
import FieldType from '@/ecm/variables/FieldType';

import FieldSearchModal from '@/components/administration/contentType/FieldSearchModal.vue';
import DateTimeField from '@/components/administration/contentType/fields/DateTimeField';
import ExternalTableField from '@/components/administration/contentType/fields/ExternalTableField';
import InternalTableField from '@/components/administration/contentType/fields/InternalTableField';
import NumericField from '@/components/administration/contentType/fields/NumericField';
import StaticListField from '@/components/administration/contentType/fields/StaticListField';
import StringField from '@/components/administration/contentType/fields/StringField';
import NumericFieldISOCompliant from './fields/NumericFieldISOCompliant.vue';
import { NUMERIC_MAX_SCALE, NUMERIC_MIN_VALUE } from '@/ecm/common/Constants';

export default {
    components: {
        FieldSearchModal,
        DateTimeField,
        ExternalTableField,
        InternalTableField,
        NumericField,
        StaticListField,
        StringField,
        NumericFieldISOCompliant,
    },
    props: {
        contentTypeFields: {
            type: Array,
            default: () => [],
        },
        contentTypeName: {
            type: String,
            default: '',
        },
    },
    created() {
        this.$v.$reset();
    },
    data() {
        return {
            fieldTypeList: [
                {
                    id: FieldType.String,
                    text: this.$t('FieldTypeString'),
                },
                {
                    id: FieldType.DateTime,
                    text: this.$t('FieldTypeDate'),
                },
                {
                    id: FieldType.Numeric,
                    text: this.$t('FieldTypeNumeric'),
                },
                {
                    id: FieldType.StaticList,
                    text: `${this.$t('FieldTypeList')} (${this.$t('Static')})`,
                },
                {
                    id: FieldType.DynamicList,
                    text: `${this.$t('FieldTypeList')} (${this.$t('ExternalTables')})`,
                },
                {
                    id: FieldType.DynamicTableList,
                    text: `${this.$t('FieldTypeList')} (${this.$t('InternalTables')})`,
                },
            ],
            isLoadingSearch: false,
            searchFields: null,
            descriptionLimit: 40,
            defFields: [],
            fieldType: FieldType,
            modal: {
                title: this.$t('Field'),
                key: 'fieldModal',
            },
            modalSearchKey: 'modalFieldSearchKey',
            open: false,
            selectedTab: 'tabGeneral',
            field: new ContentTypeField(),
            isEdit: false,
            filters: [
                '##TOP##',
                '##LIKE##',
            ]
        };
    },
    mixins: [validationMixin],
    validations: {
        field: {
            code: { required },
            name: { required },
            type: { required },
            maxLength: {
                required: requiredIf((field) => field.type === FieldType.String),
                maxValue: maxValue(4000),
            },
            cnnStr: {
                required: requiredIf((field) => field.type === FieldType.DynamicList),
            },
            dynamicTableId: {
                required: requiredIf(
                    (field) => field.type === FieldType.DynamicTableList
                ),
            },
            scale: {
                required: requiredIf((field) => field.type === FieldType.Numeric),
                maxValue: maxValue(NUMERIC_MAX_SCALE),
                minValue: minValue(0)
            },
            maxVal: {
                maxValue: Utils.validators.numericFieldMaxValue,
                minValue: minValue(NUMERIC_MIN_VALUE),
            },
            minVal: {
                maxValue: Utils.validators.numericFieldMaxValue,
                minValue: minValue(NUMERIC_MIN_VALUE),
            }
        }
    },
    computed: {
        modalSearchInstance() {
            return this.$refs[this.modalSearchKey];
        },
        modalTitle() {
            return `${this.modal.title} ${this.field.name}`;
        },
        nameErrors() {
            const errors = [];
            if (!this.$v.field.name.$dirty) return errors;
            !this.$v.field.name.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        codeErrors() {
            const errors = [];
            if (!this.$v.field.code.$dirty) return errors;
            !this.$v.field.code.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        typeErrors() {
            const errors = [];
            if (!this.$v.field.type.$dirty) return errors;
            !this.$v.field.type.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
    },
    watch: {
        searchFields(val) {
            if (!val || val.length < 3) return;

            if (this.isLoadingSearch) return;

            this.isLoadingSearch = true;

            return ContentTypeService.searchFieldsByCode(val)
                .then((data) => {
                    this.defFields = data.map((dto) => new ContentTypeField(dto));
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.isLoadingSearch = false;
                });
        },
    },
    methods: {
        openSearch() {
            this.modalSearchInstance.show();
        },
        onFieldSelect(defField) {
            this.$emit('on-imported', defField);
            this.hide();
        },
        fieldNameChange() {
            if (!this.field.code) {
                this.field.code = `${this.contentTypeName
                    .split(' ')
                    .map((i) => i.charAt(0).toUpperCase())
                    .join('')}_${snakeCase(this.field.name)}`.substring(0, 20);
            }
        },
        fieldCodeChange(defField) {
            if (defField == null) return;

            var confirm = false;
            var defFieldCode = '';

            if (typeof defField === 'object') {
                confirm = true;
                defFieldCode = defField.code;
            }

            if (typeof defField === 'string') {
                confirm = this.defFields.filter((f) => f.code == defField).length > 0;
                defFieldCode = defField;
            }

            if (confirm) {
                this.$confirm(this.$t('ImportFieldConfirmation'), {
                    title: this.$t('Import'),
                    icon: 'mdi-alert',
                    color: 'warning',
                    buttonTrueText: this.$t('Import'),
                    buttonFalseText: this.$t('Cancel'),
                }).then((res) => {
                    if (res == 1) {
                        const field = this.defFields.filter((f) => f.code == defFieldCode);
                        this.$emit('on-imported', Utils.object.toJS(field[0]));
                        this.hide();
                    } else {
                        this.field.code = '';
                    }
                });
            }
        },
        saveItem() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                if (
                    this.$v.field.name.$invalid ||
                    this.$v.field.code.$invalid ||
                    this.$v.field.type.$invalid
                ) {
                    this.selectedTab = 'tabGeneral';
                } else {
                    this.selectedTab = 'tabConfiguration';
                }
                return;
            }
            if (this.field.type == FieldType.StaticList) {
                const repeatedValues = this.checkRepeatedValues(this.field);
                if (repeatedValues.length > 0) {
                    Utils.toast.error(
                        this.$t('ContentTypeStaticListDuplicatedValueException')
                            .replace('{0}', this.field.name)
                            .replace('{1}', repeatedValues.join(', '))
                    );
                    return;
                }
            }

            const isDynamicListType =
                this.field.type === FieldType.DynamicList ||
                this.field.type === FieldType.DynamicTableList;

            const hasNoQueryDefined =
                !this.field.command &&
                !this.field.performanceQuery &&
                !this.field.noResultQuery;

            if (isDynamicListType && hasNoQueryDefined) {
                Utils.toast.error(this.$t('Site.ContentType.InternalExternalTablesFields.MustContainAQuery'));
                return;
            }
            if (isDynamicListType && this.field.performanceQuery && !this.filters.every(filter => this.field.performanceQuery.includes(filter))) {
                Utils.toast.error(this.$t('Site.ContentType.InternalExternalTablesFields.ErrorInPerformanceQuery'));
                return;
            } else if (isDynamicListType && this.field.noResultPerformanceQuery && !this.filters.every(filter => this.field.noResultPerformanceQuery.includes(filter))) {
                Utils.toast.error(this.$t('Site.ContentType.InternalExternalTablesFields.ErrorInPerformanceQuery'));
                return;
            }

            this.convertDefaultValueToString();
            this.hide();
            this.$emit('on-saved', Utils.object.toJS(this.field));
        },
        checkRepeatedValues(field) {
            const repeatedValues = [];
            const seenValues = new Set();
            field.listElements.forEach(element => {
                if (seenValues.has(element.value.toLowerCase())) {
                    repeatedValues.push(element.value);
                } else {
                    seenValues.add(element.value.toLowerCase());
                }
            });
            return repeatedValues;
        },
        new() {
            this.isEdit = false;
            this.field = new ContentTypeField();
            this.defFields = [];
            this.$v.$reset();
            this.selectedTab = 'tabGeneral';
            this.show();
        },
        edit(field) {
            this.isEdit = true;
            this.defFields = [];
            this.field = field;
            this.selectedTab = 'tabConfiguration';
            this.show();
        },
        show() {
            this.open = true;
            this.normalizeDefaultValueForDynamicTables();
        },
        hide() {
            this.open = false;
        },
        normalizeDefaultValueForDynamicTables() {
            if (this.field.type === FieldType.DynamicTableList || this.field.type === FieldType.DynamicList) {
                if (this.field.multiValue) {
                    this.field.default = this.field.default.split(',');
                }
            }
        },
        convertDefaultValueToString() {
            if (this.field.default && (this.field.type === FieldType.DynamicTableList || this.field.type === FieldType.DynamicList)) {
                if (this.field.multiValue && Array.isArray(this.field.default)) {
                    this.field.default = this.field.default.join(',');
                }
            }
        }
    },
};
</script>
